// @flow
import * as React from 'react';

type Props = {
  /** Children Node */
  children?: React.Node,
  /** Amount of time to wait */
  delay: number,
  /** Is Currently waiting */
  isWaiting?: boolean,
  /** Called when timeout is complete */
  onTimeout: () => void,
};

/**
 * A setTimeout in component form. Automatically cleans up after itself. Use it like this:
 * ```jsx
 * <Timeout isWaiting={isInSomeWaitingState} delay={5000} onTimeout={onClearSomeWaitingState}>
 *   <SomeOtherComponent/>
 * </Timeout>
 * ```
 * The timeout will restart whenever isWaiting toggles from false (or undefined) to true.
 */
const Timeout = ({ children, delay, isWaiting, onTimeout }: Props) => {
  const timer = React.useRef(null);

  React.useEffect(() => {
    if (isWaiting && !timer.current) {
      timer.current = setTimeout(onTimeout, delay);
    } else if (!isWaiting && timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [timer, isWaiting, delay, onTimeout]);

  return children || null;
};

export default Timeout;
